import type { AnswerResponseProgress, CriterionSelectDto } from '@@types/api.types';
import { CmmcLevel, CompanyType, CriteriaOperator, CriteriaType, TechnologyType } from '@@types/api.types';
import { useGetCategories } from '@queries/category.query';
import { useGetDomains } from '@queries/domain.query';
import { useGetTechnologyUses } from '@queries/technology.query';
import { useGetTrlFilterQuestions } from '@queries/trl-filter-question.query';
import { formatArrayValues, formatCriteriaDescription } from '@utils/challenge.util';
import {
  ANSWER_RESPONSE_PROGRESS_LABELS,
  ORGANIZATION_TYPE_OPTIONS,
  TECHNOLOGY_TYPE_OPTIONS,
  cmmcLevelOptions,
  countries,
} from '@utils/constants';
import { useMemo } from 'react';

type CriteriaValues = string[] | boolean | { max?: number; min?: number; value?: number };

type UseCriteriaLabelsResult = {
  formatCriteriaLabel: (criterion: CriterionSelectDto) => string;
  isLoading: boolean;
};

const isAnswerResponseProgress = (value: unknown): value is AnswerResponseProgress =>
  typeof value === 'string' && value in ANSWER_RESPONSE_PROGRESS_LABELS;

/** Narrows the polymorphic criterion values payload of a TRL Question criterion without type assertions. */
const parseTrlQuestionValues = (values: object): { answers: AnswerResponseProgress[]; questionId?: string } => {
  if (!values || Array.isArray(values)) return { answers: [] };

  const questionId = 'questionId' in values && typeof values.questionId === 'string' ? values.questionId : undefined;
  const rawAnswers = 'answers' in values && Array.isArray(values.answers) ? values.answers : [];

  return { answers: rawAnswers.filter(isAnswerResponseProgress), questionId };
};

export const useCriteriaLabels = (): UseCriteriaLabelsResult => {
  const { data: categories, isPending: categoriesLoading } = useGetCategories();
  const { data: domains, isPending: domainsLoading } = useGetDomains();
  const { data: uses, isPending: usesLoading } = useGetTechnologyUses();
  const { data: trlQuestions, isPending: trlQuestionsLoading } = useGetTrlFilterQuestions();

  const isLoading = categoriesLoading || domainsLoading || usesLoading || trlQuestionsLoading;

  const lookupMaps = useMemo(() => {
    const countryMap = new Map(countries.map((c) => [c.code, c.name]));
    const categoryMap = new Map(categories?.map((c) => [c.id, c.name]) ?? []);
    const subcategoryMap = new Map(categories?.flatMap((c) => c.subcategories?.map((s) => [s.id, s.name]) ?? []) ?? []);
    const domainMap = new Map(domains?.map((d) => [d.id, d.name]) ?? []);
    const useMap = new Map(uses?.map((u) => [u.id, u.name]) ?? []);
    const trlQuestionMap = new Map(trlQuestions?.map((q) => [q.id, q.prompt]) ?? []);

    const organizationTypeMap = new Map<CompanyType, string>(
      ORGANIZATION_TYPE_OPTIONS.map((opt) => [opt.value, opt.label]),
    );

    const technologyTypeMap = new Map<TechnologyType, string>(
      TECHNOLOGY_TYPE_OPTIONS.map((opt) => [opt.value, opt.label]),
    );

    const cmmcLevelMap = new Map<CmmcLevel, string>(cmmcLevelOptions.map((opt) => [opt.value, opt.label]));

    return {
      categoryMap,
      cmmcLevelMap,
      countryMap,
      domainMap,
      organizationTypeMap,
      subcategoryMap,
      technologyTypeMap,
      trlQuestionMap,
      useMap,
    };
  }, [categories, domains, uses, trlQuestions]);

  const formatCriteriaLabel = (criterion: CriterionSelectDto): string => {
    const { operator, type, values } = criterion;

    // TRL Question: resolve the question prompt and answer labels from the object payload
    if (type === CriteriaType.TRL_QUESTION) {
      const { answers, questionId } = parseTrlQuestionValues(values);
      const prompt = questionId ? (lookupMaps.trlQuestionMap.get(questionId) ?? 'TRL Question') : 'TRL Question';
      const answerLabels = answers.map((answer) => ANSWER_RESPONSE_PROGRESS_LABELS[answer]);
      const formattedAnswers = formatArrayValues(answerLabels, operator);
      const prefix = operator === CriteriaOperator.IS_NONE_OF ? 'Not ' : '';
      return `${prompt}: ${prefix}${formattedAnswers}`;
    }

    const criteriaValues = values as CriteriaValues;

    // Handle array values (IDs or codes that need lookup)
    if (Array.isArray(criteriaValues)) {
      const mappedValues = criteriaValues.map((value) => {
        switch (type) {
          case CriteriaType.CATEGORY:
            return lookupMaps.categoryMap.get(value) ?? value;
          case CriteriaType.CMMC_LEVEL:
            return lookupMaps.cmmcLevelMap.get(value as CmmcLevel) ?? value;
          case CriteriaType.COUNTRY:
            return lookupMaps.countryMap.get(value) ?? value;
          case CriteriaType.DOMAINS:
            return lookupMaps.domainMap.get(value) ?? value;
          case CriteriaType.ORGANIZATION_TYPE:
            return lookupMaps.organizationTypeMap.get(value as CompanyType) ?? value;
          case CriteriaType.SUBCATEGORY:
            return lookupMaps.subcategoryMap.get(value) ?? value;
          case CriteriaType.TYPE:
            return lookupMaps.technologyTypeMap.get(value as TechnologyType) ?? value;
          case CriteriaType.USES:
            return lookupMaps.useMap.get(value) ?? value;
          default:
            return value;
        }
      });

      const formattedValues = formatArrayValues(mappedValues, operator);
      return formatCriteriaDescription({ criterion, formattedValues });
    }

    // For non-array values, use the standard formatting
    return formatCriteriaDescription({ criterion });
  };

  return {
    formatCriteriaLabel,
    isLoading,
  };
};
